import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { usePortfolioAnalytics } from '../hooks/usePortfolioAnalytics';
import { useCurrencyDisplay } from '../hooks/useCurrencyDisplay';
import { useRealtimePrices } from '../hooks/useRealtimePrices';
import TimeRangeSelector from '../components/common/TimeRangeSelector';
import RiskMetricsPanel from '../components/risk/RiskMetricsPanel';
import BenchmarkComparisonChart from '../components/charts/BenchmarkComparisonChart';
import LiveIndicator from '../components/common/LiveIndicator';
import type { TimeRange } from '../components/common/TimeRangeSelector';
import type { PortfolioSnapshot } from '../api/portfolio';
import { useAppSelector } from '../store/hooks';
import { getExchangeFreshness, listExchangeConnections, syncExchange } from '../api/exchange';
import type { ExchangeFreshnessEntry } from '../api/exchange';
import type { ExchangeConnection } from '../api/exchange';
import { downloadPortfolioPDF } from '../api/portfolio-pdf';
import { getAnnotations, upsertAnnotation, deleteAnnotation } from '../api/annotations';
import type { Annotation, ThesisStatus } from '../api/annotations';
import { listAlertRulesV2, type AlertRuleV2 } from '../api/alerts';
import { getAutomationRules, type AutomationRule } from '../api/automation';
import {
  getNotificationPreferencesV2,
  getWeeklyDigestPreference,
  type DeliveryReadiness,
  type NotificationChannelStatus,
  type NotificationPreferenceMatrix,
} from '../api/notifications';
import { useRebalancing } from '../hooks/useRebalancing';
import Button from '../components/common/Button';
import AutomationOutcomeRail from '../components/common/AutomationOutcomeRail';
import InlineStatus from '../components/common/InlineStatus';
import AutomationCoverageRail from '../components/common/AutomationCoverageRail';
import PortfolioCoverageSummaryRail from '../components/common/PortfolioCoverageSummaryRail';
import PortfolioValueArrivalSummary from '../components/common/PortfolioValueArrivalSummary';
import SetupTrustHelperBand from '../components/common/SetupTrustHelperBand';
import apiClient, { getAuthRefreshState } from '../api/client';
import type { Portfolio } from '../types';
import {
  formatSetupHandoffTimestamp,
  readSetupHandoff,
  subscribeToSetupHandoff,
  type SetupHandoffContext,
} from '../lib/setupHandoff';
import {
  buildMonitoringHandoffContext,
  getNotificationReadinessSummary,
  isDriftCapableAlertRule,
  selectMonitoringLoopDecision,
  type MonitoringLoopDecision,
} from '../lib/monitoringLoop';
import { getMonitoringHandoffPath } from '../lib/monitoringHandoff';
import { resolveFreshestCorrectiveRecall } from '../lib/correctiveRecall';
import {
  captureAuthReturnContext,
  getAuthRecoveryStateFromSearch,
  stripAuthRecoveryParams,
} from '../lib/authReturnContext';
import { AUTH_SESSION_BLOCKED_MESSAGE } from '../lib/sourceProof';
import {
  buildDriftProofLabel,
  getDriftHandoffPath,
  getLargestDriftInsight,
  getMonitoringDriftDestination,
  getMonitoringDriftDominantAction,
  hasUsableDriftTargets,
} from '../lib/driftHandoff';
import { usePortfolioCoverageSummary } from '../hooks/usePortfolioCoverageSummary';
import { getPortfolioGoals } from '../api/portfolio-goals';
import { selectPortfolioValueArrivalAction } from '../lib/portfolioValueArrival';
import { buildAutomationCoverageSummary } from '../lib/automationCoverage';
import {
  readAutomationOutcomeHandoff,
  readPersistedAutomationOutcomeHandoff,
  selectLatestAutomationOutcomeHandoff,
  writePersistedAutomationOutcomeHandoff,
} from '../lib/automationOutcomeHandoff';

function formatTimestampLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatExchangeProofTimestamp(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatExchangeStaleMeta(staleHours: number | null): string {
  if (staleHours === null) return 'unknown age';
  if (staleHours >= 48) return `${Math.floor(staleHours / 24)}d stale`;
  if (staleHours >= 1) return `${staleHours}h stale`;
  return '<1h stale';
}

function getAnalyticsExchangeProofSummary(
  connection: ExchangeConnection,
  staleEntry?: ExchangeFreshnessEntry,
) {
  if (connection.status === 'error') {
    return {
      state: 'blocked' as const,
      tone: 'error' as const,
      title: `${connection.label ?? connection.exchange} sync is blocked`,
      message: connection.last_synced_at
        ? `Provider errors are blocking fresher proof. Analytics still relies on the last successful ${formatExchangeProofTimestamp(connection.last_synced_at)} snapshot until /import proves another sync.`
        : 'Saved credentials exist, but provider errors are blocking the first successful sync proof on /import.',
      meta: connection.error_message ?? `Added ${formatExchangeProofTimestamp(connection.created_at)}`,
    };
  }

  if (staleEntry && connection.last_synced_at) {
    return {
      state: 'stale' as const,
      tone: 'warning' as const,
      title: `${connection.label ?? connection.exchange} data is stale`,
      message: `Analytics still relies on the last successful ${formatExchangeProofTimestamp(connection.last_synced_at)} snapshot until a fresh sync completes.`,
      meta: formatExchangeStaleMeta(staleEntry.staleHours),
    };
  }

  if (connection.status === 'active' && !connection.last_synced_at) {
    return {
      state: 'not yet proven' as const,
      tone: 'info' as const,
      title: `${connection.label ?? connection.exchange} sync is not yet proven`,
      message: 'Saved credentials exist, but no successful sync is on record yet. Treat exchange-backed analytics as unproven until /import completes one sync.',
      meta: `Added ${formatExchangeProofTimestamp(connection.created_at)}`,
    };
  }

  return {
    state: 'synced' as const,
    tone: 'success' as const,
    title: `${connection.label ?? connection.exchange} synced`,
    message: `Last successful sync confirmed ${formatExchangeProofTimestamp(connection.last_synced_at)}.`,
    meta: `Last synced ${formatExchangeProofTimestamp(connection.last_synced_at)}`,
  };
}

interface BenchmarkPoint {
  /** 0-based index matching snapshot index */
  index: number;
  value: number;
}

// Compute benchmark line points from a start/end % change spread across n snapshots
function buildBenchmarkPoints(
  snapshotCount: number,
  startValue: number,
  changePct: number,
): BenchmarkPoint[] {
  if (snapshotCount < 2) return [];
  const endValue = startValue * (1 + changePct / 100);
  const points: BenchmarkPoint[] = [];
  for (let i = 0; i < snapshotCount; i++) {
    const t = i / (snapshotCount - 1);
    points.push({ index: i, value: startValue + (endValue - startValue) * t });
  }
  return points;
}

// Simple SVG line chart for portfolio value over time with optional BTC/ETH overlays
function PerformanceChart({
  snapshots,
  btcChangePct,
  ethChangePct,
  formatValue,
}: {
  snapshots: PortfolioSnapshot[];
  btcChangePct: number | null;
  ethChangePct: number | null;
  formatValue: (v: number, options?: { compact?: boolean }) => string;
}) {
  if (snapshots.length < 2) {
    return (
      <div className="h-48 flex items-center justify-center text-gray-500 text-sm">
        Not enough data to draw chart
      </div>
    );
  }

  const portfolioValues = snapshots.map((s) => s.total_value_usd);
  const startPortfolio = portfolioValues[0];

  // Benchmark lines use normalised values anchored to the portfolio start value
  const btcPoints =
    btcChangePct != null
      ? buildBenchmarkPoints(snapshots.length, startPortfolio, btcChangePct)
      : [];
  const ethPoints =
    ethChangePct != null
      ? buildBenchmarkPoints(snapshots.length, startPortfolio, ethChangePct)
      : [];

  // Compute overall min/max across all series for a shared Y axis
  const allValues = [
    ...portfolioValues,
    ...btcPoints.map((p) => p.value),
    ...ethPoints.map((p) => p.value),
  ];
  const minVal = Math.min(...allValues);
  const maxVal = Math.max(...allValues);
  const valRange = maxVal - minVal || 1;

  const WIDTH = 800;
  const HEIGHT = 200;
  const PADDING_X = 10;
  const PADDING_Y = 10;

  const toSvgX = (i: number) =>
    PADDING_X + (i / (snapshots.length - 1)) * (WIDTH - 2 * PADDING_X);
  const toSvgY = (v: number) =>
    HEIGHT - PADDING_Y - ((v - minVal) / valRange) * (HEIGHT - 2 * PADDING_Y);

  const portfolioPoints = snapshots.map((s, i) => ({
    x: toSvgX(i),
    y: toSvgY(s.total_value_usd),
  }));

  const pathData = portfolioPoints
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(' ');

  const hasGained = portfolioValues[portfolioValues.length - 1] >= startPortfolio;
  const areaData =
    pathData +
    ` L ${portfolioPoints[portfolioPoints.length - 1].x.toFixed(1)} ${(HEIGHT - PADDING_Y).toFixed(1)} L ${portfolioPoints[0].x.toFixed(1)} ${(HEIGHT - PADDING_Y).toFixed(1)} Z`;
  const fillColor = hasGained ? 'rgba(52,211,153,0.15)' : 'rgba(239,68,68,0.15)';

  const btcPath =
    btcPoints.length > 1
      ? btcPoints
          .map((p, i) => `${i === 0 ? 'M' : 'L'} ${toSvgX(p.index).toFixed(1)} ${toSvgY(p.value).toFixed(1)}`)
          .join(' ')
      : null;
  const ethPath =
    ethPoints.length > 1
      ? ethPoints
          .map((p, i) => `${i === 0 ? 'M' : 'L'} ${toSvgX(p.index).toFixed(1)} ${toSvgY(p.value).toFixed(1)}`)
          .join(' ')
      : null;

  const firstDate = new Date(snapshots[0].timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const lastDate = new Date(snapshots[snapshots.length - 1].timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return (
    <div className="relative w-full">
      {/* Legend */}
      <div className="flex items-center gap-4 mb-2 text-xs">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-4 h-0.5 bg-blue-500" />
          <span className="text-gray-400">Portfolio</span>
        </span>
        {btcPath && (
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-4" style={{ borderTop: '2px dashed #fbbf24' }} />
            <span className="text-gray-400">BTC ({btcChangePct != null ? (btcChangePct >= 0 ? '+' : '') + btcChangePct.toFixed(2) + '%' : ''})</span>
          </span>
        )}
        {ethPath && (
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-4" style={{ borderTop: '2px dashed #a78bfa' }} />
            <span className="text-gray-400">ETH ({ethChangePct != null ? (ethChangePct >= 0 ? '+' : '') + ethChangePct.toFixed(2) + '%' : ''})</span>
          </span>
        )}
      </div>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="none"
        className="w-full h-48"
        aria-label="Portfolio value over time with BTC and ETH benchmarks"
      >
        {/* Area fill */}
        <path d={areaData} fill={fillColor} />
        {/* BTC benchmark line */}
        {btcPath && (
          <path
            d={btcPath}
            fill="none"
            stroke="#fbbf24"
            strokeWidth="1.5"
            strokeDasharray="6 3"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.8"
          />
        )}
        {/* ETH benchmark line */}
        {ethPath && (
          <path
            d={ethPath}
            fill="none"
            stroke="#a78bfa"
            strokeWidth="1.5"
            strokeDasharray="6 3"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.8"
          />
        )}
        {/* Portfolio line (on top) */}
        <path
          d={pathData}
          fill="none"
          stroke="#3b82f6"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {/* Axis labels */}
      <div className="flex justify-between mt-1 px-1">
        <span className="text-xs text-gray-500">{firstDate}</span>
        <span className="text-xs text-gray-500">{lastDate}</span>
      </div>
      <div className="flex justify-between mt-0.5 px-1">
        <span className="text-xs text-gray-500 font-mono">{formatValue(minVal)}</span>
        <span className="text-xs text-gray-500 font-mono">{formatValue(maxVal)}</span>
      </div>
    </div>
  );
}

interface MetricCardProps {
  label: string;
  value: number | null;
  isLoading: boolean;
  colorClass: string;
  suffix?: string;
}

function MetricCard({ label, value, isLoading, colorClass, suffix = '%' }: MetricCardProps) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 md:p-6">
      <p className="text-xs text-gray-400 mb-2">{label}</p>
      {isLoading ? (
        <div className="skeleton h-8 w-24" />
      ) : value == null ? (
        <p className="text-2xl font-bold font-mono tabular-nums text-gray-500">—</p>
      ) : (
        <p className={`text-2xl font-bold font-mono tabular-nums ${colorClass}`}>
          {value >= 0 ? '+' : ''}
          {value.toFixed(2)}
          {suffix}
        </p>
      )}
    </div>
  );
}

interface BenchmarkConsequenceSummary {
  tone: 'info' | 'success' | 'warning';
  title: string;
  message: string;
  proof: Array<{
    label: string;
    value: string;
  }>;
}

function formatSignedPercent(value: number | null | undefined) {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }

  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
}

function formatPointGap(value: number) {
  return `${Math.abs(value).toFixed(2)} pts`;
}

function joinBenchmarkNames(names: string[]) {
  if (names.length <= 1) {
    return names[0] ?? 'benchmarks';
  }

  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`;
  }

  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

function buildBenchmarkConsequenceSummary({
  activeRangeContext,
  historyCount,
  twrValue,
  btcChangePct,
  ethChangePct,
  benchmarkGeneratedAt,
  followUpLabel,
}: {
  activeRangeContext: string;
  historyCount: number;
  twrValue: number | null;
  btcChangePct: number | null;
  ethChangePct: number | null;
  benchmarkGeneratedAt: string | null;
  followUpLabel: string;
}): BenchmarkConsequenceSummary | null {
  if (twrValue == null && historyCount < 2 && btcChangePct == null && ethChangePct == null) {
    return null;
  }

  const proof: BenchmarkConsequenceSummary['proof'] = [
    { label: 'Window', value: activeRangeContext },
    { label: 'Saved history', value: `${historyCount} snapshots` },
  ];

  if (twrValue != null) {
    proof.unshift({ label: 'Portfolio', value: formatSignedPercent(twrValue) });
  }

  if (btcChangePct != null) {
    proof.push({ label: 'BTC', value: formatSignedPercent(btcChangePct) });
  }

  if (ethChangePct != null) {
    proof.push({ label: 'ETH', value: formatSignedPercent(ethChangePct) });
  }

  if (benchmarkGeneratedAt) {
    proof.push({
      label: 'Benchmarks',
      value: formatExchangeProofTimestamp(benchmarkGeneratedAt),
    });
  }

  if (twrValue == null || historyCount < 2) {
    return {
      tone: 'warning',
      title: 'Benchmark consequence needs more history',
      message: `The selected ${activeRangeContext} view does not yet have enough saved performance proof to explain the move against BTC or ETH. ${followUpLabel} stays the honest next review until a broader or fresher window is available.`,
      proof,
    };
  }

  const comparisons = [
    btcChangePct != null ? { name: 'BTC', delta: twrValue - btcChangePct } : null,
    ethChangePct != null ? { name: 'ETH', delta: twrValue - ethChangePct } : null,
  ].filter((value): value is { name: string; delta: number } => value !== null);

  if (!comparisons.length) {
    return {
      tone: 'info',
      title: 'Benchmark consequence is still limited',
      message: `Portfolio returned ${formatSignedPercent(twrValue)} over ${activeRangeContext}, but benchmark comparison is unavailable in this view. ${followUpLabel} remains the honest next review while Pinecurl keeps the mounted proof stack visible.`,
      proof,
    };
  }

  const ahead = comparisons.filter((item) => item.delta > 0);
  const behind = comparisons.filter((item) => item.delta < 0);

  if (ahead.length === comparisons.length) {
    return {
      tone: 'success',
      title: `Portfolio is ahead of ${joinBenchmarkNames(ahead.map((item) => item.name))}`,
      message: `Portfolio returned ${formatSignedPercent(twrValue)} over ${activeRangeContext}, beating ${ahead
        .map((item) => `${item.name} by ${formatPointGap(item.delta)}`)
        .join(' and ')}. ${followUpLabel} is the next review to confirm which holdings are carrying the lead.`,
      proof,
    };
  }

  if (behind.length === comparisons.length) {
    return {
      tone: 'warning',
      title: `Portfolio is trailing ${joinBenchmarkNames(behind.map((item) => item.name))}`,
      message: `Portfolio returned ${formatSignedPercent(twrValue)} over ${activeRangeContext}, trailing ${behind
        .map((item) => `${item.name} by ${formatPointGap(item.delta)}`)
        .join(' and ')}. ${followUpLabel} is the next review to inspect what is dragging the result.`,
      proof,
    };
  }

  const aheadNames = ahead.map((item) => item.name);
  const behindNames = behind.map((item) => item.name);

  return {
    tone: 'info',
    title: `Portfolio is ahead of ${joinBenchmarkNames(aheadNames)} but trailing ${joinBenchmarkNames(behindNames)}`,
    message: `Portfolio returned ${formatSignedPercent(twrValue)} over ${activeRangeContext}, beating ${ahead
      .map((item) => `${item.name} by ${formatPointGap(item.delta)}`)
      .join(' and ')} while trailing ${behind
      .map((item) => `${item.name} by ${formatPointGap(item.delta)}`)
      .join(' and ')}. ${followUpLabel} is the next review to explain the spread before you rely on the chart alone.`,
    proof,
  };
}

const PortfolioAnalyticsPage: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [range, setRange] = useState<TimeRange>('30d');
  // Custom date range state — Chunk 158
  const [rangeMode, setRangeMode] = useState<'preset' | 'custom'>('preset');
  const [customFromStr, setCustomFromStr] = useState<string>(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [customToStr, setCustomToStr] = useState<string>(() => new Date().toISOString().slice(0, 10));

  const customFromDate = useMemo(
    () => (rangeMode === 'custom' ? new Date(customFromStr) : undefined),
    [rangeMode, customFromStr],
  );
  const customToDate = useMemo(
    () => (rangeMode === 'custom' ? new Date(customToStr) : undefined),
    [rangeMode, customToStr],
  );

  const activeRangeContext = useMemo(() => {
    if (rangeMode === 'custom') {
      return customFromStr && customToStr
        ? `${customFromStr} → ${customToStr}`
        : 'custom range';
    }
    return range;
  }, [customFromStr, customToStr, range, rangeMode]);

  const {
    data,
    isLoading,
    isRefreshing,
    error,
    displayedContext,
    lastUpdatedAt,
    refetch,
  } = usePortfolioAnalytics(range, customFromDate, customToDate, activeRangeContext);
  const { performance, history, volatility, benchmarks } = data;
  const { user } = useAppSelector((state) => state.auth);
  const { formatValue } = useCurrencyDisplay();

  // Real-time price feed connection status (Chunk 108)
  const { connected: priceStreamConnected } = useRealtimePrices();

  // Exchange freshness for staleness banner (Chunk 92)
  const [staleConnections, setStaleConnections] = useState<ExchangeFreshnessEntry[]>([]);
  const [exchangeConnections, setExchangeConnections] = useState<ExchangeConnection[]>([]);
  const [syncingStaleId, setSyncingStaleId] = useState<string | null>(null);

  // Annotations state (Chunk 141)
  const [annotations, setAnnotations] = useState<Record<string, Annotation>>({});
  const [annotationPanelSymbol, setAnnotationPanelSymbol] = useState<string | null>(null);
  const [annotationDraft, setAnnotationDraft] = useState<{
    thesisStatus: ThesisStatus;
    personalTargetPrice: string;
    thesis: string;
  }>({ thesisStatus: 'neutral', personalTargetPrice: '', thesis: '' });
  const [annotationAction, setAnnotationAction] = useState<'save' | 'delete' | null>(null);
  const [annotationStatus, setAnnotationStatus] = useState<{
    tone: 'info' | 'success' | 'error';
    title: string;
    message: string;
  } | null>(null);

  // PDF export state (Chunk 94)
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfStatus, setPdfStatus] = useState<{
    tone: 'info' | 'success' | 'error';
    title: string;
    message: string;
    meta?: string;
  } | null>(null);
  const [exchangeSyncStatus, setExchangeSyncStatus] = useState<Record<string, {
    exchange: string;
    tone: 'info' | 'success' | 'error';
    title: string;
    message: string;
  }>>({});
  const analyticsEvidenceRef = useRef<HTMLDivElement>(null);
  const [portfolioSummary, setPortfolioSummary] = useState<Portfolio | null>(null);
  const [portfolioSummaryLoading, setPortfolioSummaryLoading] = useState(true);
  const [setupHandoff, setSetupHandoff] = useState<SetupHandoffContext | null>(null);
  const [alertRules, setAlertRules] = useState<AlertRuleV2[] | null>(null);
  const [automationRules, setAutomationRules] = useState<AutomationRule[] | null>(null);
  const [automationCoverageError, setAutomationCoverageError] = useState(false);
  const [activeGoalName, setActiveGoalName] = useState<string | null>(null);
  const [activeGoalUpdatedAt, setActiveGoalUpdatedAt] = useState<string | null>(null);
  const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreferenceMatrix | null>(null);
  const [notificationChannelStatus, setNotificationChannelStatus] = useState<NotificationChannelStatus | null>(null);
  const [weeklyDigestEnabled, setWeeklyDigestEnabled] = useState<boolean | null>(null);
  const [digestReadiness, setDigestReadiness] = useState<DeliveryReadiness | null>(null);
  const { advisor: rebalanceAdvisor } = useRebalancing(user?.id);

  useEffect(() => {
    if (!user?.id) return;
    setPortfolioSummaryLoading(true);
    apiClient.get<Portfolio>(`/analytics/portfolio/${user.id}`)
      .then((response) => setPortfolioSummary(response.data))
      .catch(() => setPortfolioSummary(null))
      .finally(() => setPortfolioSummaryLoading(false));
  }, [user?.id]);

  useEffect(() => {
    setSetupHandoff(readSetupHandoff(user?.id));
  }, [user?.id]);

  useEffect(() => subscribeToSetupHandoff(user?.id, setSetupHandoff), [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setAlertRules(null);
      setAutomationRules(null);
      setAutomationCoverageError(false);
      setActiveGoalName(null);
      setActiveGoalUpdatedAt(null);
      return;
    }

    listAlertRulesV2(user.id)
      .then((rules) => setAlertRules(rules))
      .catch(() => setAlertRules([]));

    setAutomationRules(null);
    setAutomationCoverageError(false);
    getAutomationRules(user.id, true)
      .then((rules) => {
        setAutomationRules(Array.isArray(rules) ? rules : []);
        setAutomationCoverageError(false);
      })
      .catch(() => {
        setAutomationRules([]);
        setAutomationCoverageError(true);
      });

    getPortfolioGoals(user.id)
      .then((response) => {
        const activeGoal = response.goals.find((goal) => goal.isActive) ?? null;
        setActiveGoalName(activeGoal?.name ?? null);
        setActiveGoalUpdatedAt(activeGoal?.updatedAt ?? null);
      })
      .catch(() => {
        setActiveGoalName(null);
        setActiveGoalUpdatedAt(null);
      });
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setNotificationPreferences(null);
      setNotificationChannelStatus(null);
      setWeeklyDigestEnabled(null);
      setDigestReadiness(null);
      return;
    }

    getNotificationPreferencesV2()
      .then((response) => {
        setNotificationPreferences(response.preferences);
        setNotificationChannelStatus(response.channelStatus);
      })
      .catch(() => {
        setNotificationPreferences(null);
        setNotificationChannelStatus(null);
      });

    getWeeklyDigestPreference()
      .then((response) => {
        setWeeklyDigestEnabled(response.weeklyAlertDigestEnabled);
        setDigestReadiness(response.deliveryReadiness);
      })
      .catch(() => {
        setWeeklyDigestEnabled(null);
        setDigestReadiness(null);
      });
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    listExchangeConnections(user.id)
      .then((data) => setExchangeConnections(data.connections ?? []))
      .catch(() => setExchangeConnections([]));
    getExchangeFreshness(user.id)
      .then((data) => setStaleConnections(data.freshness.filter((f) => f.isStale)))
      .catch(() => setStaleConnections([]));
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    getAnnotations(user.id)
      .then((data) => {
        const map: Record<string, Annotation> = {};
        data.annotations.forEach((a) => { map[a.symbol] = a; });
        setAnnotations(map);
      })
      .catch(() => undefined);
  }, [user?.id]);

  const handleExportPdf = async () => {
    if (!user?.id) return;
    setPdfLoading(true);
      setPdfStatus({
        tone: 'info',
        title: 'Preparing PDF export',
        message: `Building a PDF for the ${activeRangeContext} analytics view. Your selected range stays visible while it downloads.`,
        meta: `${activeRangeContext} · requested ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
      });
    try {
      await downloadPortfolioPDF(user.id);
      setPdfStatus({
        tone: 'success',
        title: 'PDF downloaded',
        message: `The ${activeRangeContext} analytics summary was exported successfully.`,
        meta: `${activeRangeContext} · completed ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
      });
    } catch {
      setPdfStatus({
        tone: 'error',
        title: 'PDF export failed',
        message: `We could not generate the ${activeRangeContext} PDF. Retry when you are ready.`,
        meta: `${activeRangeContext} · failed ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const openAnnotationPanel = (symbol: string) => {
    const existing = annotations[symbol];
    setAnnotationDraft({
      thesisStatus: existing?.thesisStatus ?? 'neutral',
      personalTargetPrice: existing?.personalTargetPrice != null ? String(existing.personalTargetPrice) : '',
      thesis: existing?.thesis ?? '',
    });
    setAnnotationStatus(null);
    setAnnotationAction(null);
    setAnnotationPanelSymbol(symbol);
  };

  const closeAnnotationPanel = () => {
    setAnnotationPanelSymbol(null);
    setAnnotationStatus(null);
    setAnnotationAction(null);
  };

  const saveAnnotation = async () => {
    if (!user?.id || !annotationPanelSymbol) return;
    setAnnotationAction('save');
    setAnnotationStatus({
      tone: 'info',
      title: `${annotationPanelSymbol} thesis saving`,
      message: 'Saving your thesis, target price, and outlook without leaving this panel.',
    });
    try {
      const targetPrice = annotationDraft.personalTargetPrice.trim()
        ? Number(annotationDraft.personalTargetPrice)
        : null;
      const result = await upsertAnnotation(user.id, annotationPanelSymbol, {
        thesisStatus: annotationDraft.thesisStatus,
        personalTargetPrice: targetPrice,
        thesis: annotationDraft.thesis || null,
      });
      setAnnotations((prev) => ({ ...prev, [annotationPanelSymbol]: result.annotation }));
      setAnnotationStatus({
        tone: 'success',
        title: `${annotationPanelSymbol} thesis saved`,
        message: 'Your attribution note is now saved and stays attached to this asset.',
      });
    } catch {
      setAnnotationStatus({
        tone: 'error',
        title: `${annotationPanelSymbol} thesis not saved`,
        message: 'We could not save this attribution note. Review the fields and try again.',
      });
    } finally {
      setAnnotationAction(null);
    }
  };

  const removeAnnotation = async () => {
    if (!user?.id || !annotationPanelSymbol) return;
    setAnnotationAction('delete');
    setAnnotationStatus({
      tone: 'info',
      title: `${annotationPanelSymbol} thesis removing`,
      message: 'Removing the saved attribution note for this asset.',
    });
    try {
      await deleteAnnotation(user.id, annotationPanelSymbol);
      setAnnotations((prev) => {
        const next = { ...prev };
        delete next[annotationPanelSymbol];
        return next;
      });
      setAnnotationDraft({
        thesisStatus: 'neutral',
        personalTargetPrice: '',
        thesis: '',
      });
      setAnnotationStatus({
        tone: 'success',
        title: `${annotationPanelSymbol} thesis removed`,
        message: 'This asset no longer has a saved thesis. You can add a new note before closing the panel.',
      });
    } catch {
      setAnnotationStatus({
        tone: 'error',
        title: `${annotationPanelSymbol} thesis not removed`,
        message: 'We could not delete this attribution note. Try again when you are ready.',
      });
    } finally {
      setAnnotationAction(null);
    }
  };

  const handleSyncStale = (stale: ExchangeFreshnessEntry) => {
    setSyncingStaleId(stale.connectionId);
    setExchangeSyncStatus((current) => ({
      ...current,
      [stale.connectionId]: {
        exchange: stale.exchange,
        tone: 'info',
        title: `Syncing ${stale.exchange}`,
        message: `Refreshing ${stale.exchange} balances while your ${activeRangeContext} analytics context stays visible.`,
      },
    }));
    syncExchange(stale.connectionId)
      .then(() => {
        setExchangeSyncStatus((current) => ({
          ...current,
          [stale.connectionId]: {
            exchange: stale.exchange,
            tone: 'success',
            title: `${stale.exchange} sync started`,
            message: `${stale.exchange} accepted the refresh request. You can keep reviewing this page while fresh balances arrive.`,
          },
        }));
        if (user?.id) {
          return getExchangeFreshness(user.id).then((freshnessData) =>
            setStaleConnections(freshnessData.freshness.filter((f) => f.isStale))
          );
        }
        return undefined;
      })
      .catch(() => {
        setExchangeSyncStatus((current) => ({
          ...current,
          [stale.connectionId]: {
            exchange: stale.exchange,
            tone: 'error',
            title: `${stale.exchange} sync failed`,
            message: `We could not start a fresh sync for ${stale.exchange}. Try this action again when the connection is responsive.`,
          },
        }));
      })
      .finally(() => setSyncingStaleId(null));
  };

  const lingeringExchangeStatuses = Object.entries(exchangeSyncStatus).filter(
    ([connectionId]) => !staleConnections.some((stale) => stale.connectionId === connectionId),
  );
  const staleConnectionMap = useMemo(
    () => Object.fromEntries(staleConnections.map((entry) => [entry.connectionId, entry])),
    [staleConnections],
  );
  const exchangeProofGaps = useMemo(
    () =>
      exchangeConnections
        .map((connection) => ({
          connection,
          proof: getAnalyticsExchangeProofSummary(connection, staleConnectionMap[connection.id]),
        }))
        .filter(({ proof }) => proof.state === 'blocked' || proof.state === 'not yet proven'),
    [exchangeConnections, staleConnectionMap],
  );
  const topExchangeProofGap = exchangeProofGaps[0]?.proof ?? null;

  const attribution = performance?.attribution ?? [];
  const sortedByReturn = [...attribution].sort((a, b) => b.return - a.return);
  const maxAbsReturn = sortedByReturn.reduce(
    (max, a) => Math.max(max, Math.abs(a.return)),
    1,
  );

  const twrValue = performance?.twr ?? null;
  const mwrValue = performance?.mwr ?? null;
  const totalReturnValue = performance?.totalReturn ?? null;
  const annualizedVol = volatility?.annualized ?? null;
  const btcChangePct = benchmarks?.btc ?? null;
  const ethChangePct = benchmarks?.eth ?? null;
  const hasConnectedPortfolio = Boolean(
    portfolioSummary && (portfolioSummary.tokens.length > 0 || portfolioSummary.totalValueUsd > 0)
  );
  const showNoPortfolioState = !portfolioSummaryLoading && portfolioSummary !== null && !hasConnectedPortfolio;
  const analyticsErrorTone = error && performance === null && history === null && volatility === null ? 'error' : 'warning';
  const hasAnalyticsData = Boolean(performance || history || volatility || benchmarks);
  const authRefreshState = getAuthRefreshState();
  const authRecoveryState = getAuthRecoveryStateFromSearch(location.pathname, location.search);
  const showInitialAnalyticsLoad = isLoading && !hasAnalyticsData;
  const displayedRangeContext = displayedContext?.label ?? activeRangeContext;
  const displayedAtLabel = formatTimestampLabel(lastUpdatedAt);
  const canBroadenRange = rangeMode === 'custom' || range !== 'all';
  const broadenRange = () => {
    setRangeMode('preset');
    setRange('all');
  };
  const handleAnalyticsCheckAction = useCallback(() => {
    if (canBroadenRange) {
      broadenRange();
      return;
    }

    void refetch();
  }, [broadenRange, canBroadenRange, refetch]);
  const analyticsCheckActionCopy = useMemo(() => {
    if (canBroadenRange) {
      return {
        helper:
          rangeMode === 'custom'
            ? 'This page is already open. Expand from the custom window to the full saved timeline before retrying.'
            : 'This page is already open. Widen the selected time window to look for saved snapshots.',
      };
    }

    return {
      helper: 'This page is already open. Retry the current analytics range without leaving it.',
    };
  }, [canBroadenRange, rangeMode]);
  const notificationReadinessSummary = useMemo(
    () =>
      getNotificationReadinessSummary({
        preferences: notificationPreferences,
        channelStatus: notificationChannelStatus,
        weeklyDigestEnabled,
        digestReadiness,
      }),
    [digestReadiness, notificationChannelStatus, notificationPreferences, weeklyDigestEnabled],
  );
  const analyticsCorrectiveRecall = useMemo(
    () => resolveFreshestCorrectiveRecall(user?.id, 'monitoring'),
    [user?.id],
  );
  const analyticsMonitoringDecision = useMemo(
    () =>
      selectMonitoringLoopDecision({
        setupHandoff,
        hasPortfolioSnapshot: hasConnectedPortfolio,
        hasAnalyticsData,
        exchangeConnections,
        staleConnections,
        activeTargetCount: rebalanceAdvisor?.targets.length ?? 0,
        driftedAssetCount:
          rebalanceAdvisor?.drifts.filter((drift) => drift.driftStatus !== 'within').length ?? 0,
        activeGoalName,
        alertRulesCount: alertRules?.length ?? null,
        enabledAlertRulesCount: alertRules?.filter((rule) => rule.enabled).length ?? null,
        automationRulesCount: automationRules?.length ?? null,
        activeAutomationRulesCount: automationRules?.filter((rule) => rule.is_active).length ?? null,
        driftCoverageActive:
          alertRules === null
            ? null
            : alertRules.some((rule) => rule.enabled && isDriftCapableAlertRule(rule)),
        notificationReadiness: notificationReadinessSummary,
        recalledCorrectiveProof: analyticsCorrectiveRecall,
      }),
    [
      activeGoalName,
      alertRules,
      analyticsCorrectiveRecall,
      automationRules,
      exchangeConnections,
      hasAnalyticsData,
      hasConnectedPortfolio,
      notificationReadinessSummary,
      rebalanceAdvisor,
      setupHandoff,
      staleConnections,
    ],
  );
  const analyticsDriftHandoffPath = useMemo(() => {
    if (!rebalanceAdvisor) {
      return '/rebalancing';
    }

    const hasSavedTargets = hasUsableDriftTargets(
      rebalanceAdvisor.targets.map((target) => target.targetPct),
    );
    const driftingAssets = rebalanceAdvisor.drifts.filter((drift) => drift.driftStatus !== 'within');
    const destination = getMonitoringDriftDestination({
      activeGoalName,
      hasUsableTargets: hasSavedTargets,
    });
    const largestDrift = getLargestDriftInsight(
      rebalanceAdvisor.drifts,
      (drift) => drift.symbol,
      (drift) => drift.driftPct,
    );

    return getDriftHandoffPath({
      source: 'portfolio-analytics',
      destination,
      proofLabel: buildDriftProofLabel({
        activeGoalName,
        driftedAssetCount: driftingAssets.length,
        usableTargetCount: rebalanceAdvisor.targets.length,
      }),
      proofOccurredAt: lastUpdatedAt ?? activeGoalUpdatedAt,
      activeGoalName,
      driftedAssetCount: driftingAssets.length,
      topDriftSymbol: largestDrift.symbol,
      topDriftPct: largestDrift.driftPct,
      dominantAction: getMonitoringDriftDominantAction({
        destination,
        hasUsableTargets: hasSavedTargets,
        hasSuggestedTrades: driftingAssets.some((drift) => Math.abs(drift.suggestedTradeUsd) > 0.01),
      }),
    });
  }, [activeGoalName, activeGoalUpdatedAt, lastUpdatedAt, rebalanceAdvisor]);
  const renderMonitoringDecisionMeta = useCallback(
    (decision: MonitoringLoopDecision) => {
      const setupUpdatedAt =
        !decision.isCorrectiveRecall && setupHandoff ? formatSetupHandoffTimestamp(setupHandoff.occurredAt) : null;
      return (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {decision.proof.map((item) => (
            <span key={`${decision.key}-${item.label}`}>
              {item.label}:{' '}
              <span className="font-mono tabular-nums text-gray-200">{item.value}</span>
            </span>
          ))}
          {setupUpdatedAt ? (
            <span>
              Updated:{' '}
              <span className="font-mono tabular-nums text-gray-200">{setupUpdatedAt}</span>
            </span>
          ) : null}
        </div>
      );
    },
    [setupHandoff],
  );
  const coverageSummary = usePortfolioCoverageSummary(user?.id, { surface: 'monitoring' });
  const queryAutomationOutcomeHandoff = useMemo(
    () => readAutomationOutcomeHandoff(searchParams, 'portfolio-analytics'),
    [searchParams],
  );
  const persistedAutomationOutcomeHandoff = useMemo(
    () => readPersistedAutomationOutcomeHandoff('portfolio-analytics', user?.id),
    [user?.id],
  );
  const automationOutcomeHandoff = useMemo(
    () => selectLatestAutomationOutcomeHandoff(queryAutomationOutcomeHandoff, persistedAutomationOutcomeHandoff),
    [persistedAutomationOutcomeHandoff, queryAutomationOutcomeHandoff],
  );
  const automationCoverageSummary = useMemo(
    () =>
      buildAutomationCoverageSummary({
        rules: automationRules,
        isLoading: Boolean(user?.id) && automationRules === null && !automationCoverageError,
        hasError: automationCoverageError,
      }),
    [automationCoverageError, automationRules, user?.id],
  );
  const hasLoadedPortfolioValue = Boolean(portfolioSummary && portfolioSummary.totalValueUsd > 0);
  const hasHistoryForBenchmarkContext = (history?.snapshots?.length ?? 0) > 1;
  const analyticsMonitoringHandoff = useMemo(
    () =>
      buildMonitoringHandoffContext({
        source: 'portfolio-analytics',
        setupHandoff,
        activeTargetCount: rebalanceAdvisor?.targets.length ?? 0,
        driftedAssetCount:
          rebalanceAdvisor?.drifts.filter((drift) => drift.driftStatus !== 'within').length ?? 0,
        activeGoalName,
        proofOccurredAt: lastUpdatedAt ?? activeGoalUpdatedAt ?? setupHandoff?.occurredAt ?? null,
        automationRulesCount: automationRules?.length ?? null,
        driftCoverageActive:
          alertRules === null
            ? null
            : alertRules.some((rule) => rule.enabled && isDriftCapableAlertRule(rule)),
        notificationReadiness: notificationReadinessSummary,
      }),
    [activeGoalName, alertRules, automationRules, notificationReadinessSummary, rebalanceAdvisor, setupHandoff],
  );
  const analyticsArrivalActionDecision = useMemo(
    () =>
      hasLoadedPortfolioValue && analyticsMonitoringDecision?.isCorrectiveRecall
        ? {
            source: 'monitoring' as const,
            key: analyticsMonitoringDecision.key,
          }
        : hasLoadedPortfolioValue
        ? selectPortfolioValueArrivalAction(
            coverageSummary,
            analyticsMonitoringDecision,
            'Check Analytics',
            {
              hasLoadedPortfolioValue,
              loadedActionLabel: 'Check Analytics',
              preferMonitoringCheck:
                analyticsMonitoringDecision?.key === 'check-analytics' &&
                !hasHistoryForBenchmarkContext,
            },
          )
        : null,
    [
      analyticsMonitoringDecision,
      coverageSummary,
      hasHistoryForBenchmarkContext,
      hasLoadedPortfolioValue,
    ],
  );
  const renderAnalyticsPortfolioReviewAction = useCallback(
    (variant: 'primary' | 'outline' = 'primary') => (
      <Button
        id={variant === 'primary' ? 'analytics-owner-cta' : undefined}
        size="sm"
        variant={variant === 'primary' ? undefined : variant}
        onClick={handleAnalyticsCheckAction}
      >
        Check Analytics
      </Button>
    ),
    [handleAnalyticsCheckAction],
  );
  const renderAnalyticsMonitoringAction = useCallback(
    (decision: MonitoringLoopDecision, variant: 'primary' | 'outline' = 'primary') => {
      if (decision.key === 'review-drift') {
        return (
          <Button
            id={variant === 'primary' ? 'analytics-owner-cta' : undefined}
            size="sm"
            variant={variant === 'primary' ? undefined : variant}
            onClick={() => navigate(analyticsDriftHandoffPath)}
          >
            {decision.title}
          </Button>
        );
      }

      if (
        decision.actionPath &&
        decision.key !== 'check-analytics' &&
        decision.key !== 'enable-alerts' &&
        decision.key !== 'open-automation' &&
        decision.key !== 'review-active-rules'
      ) {
        return (
          <Button
            id={variant === 'primary' ? 'analytics-owner-cta' : undefined}
            size="sm"
            variant={variant === 'primary' ? undefined : variant}
            onClick={() => navigate(decision.actionPath!)}
          >
            {decision.title}
          </Button>
        );
      }

      if (decision.key === 'check-analytics') {
        return (
          <div className="flex max-w-xs flex-col items-start gap-1.5">
            <Button
              id={variant === 'primary' ? 'analytics-owner-cta' : undefined}
              size="sm"
              variant={variant === 'primary' ? undefined : variant}
              onClick={handleAnalyticsCheckAction}
            >
              {decision.title}
            </Button>
            <p className="text-xs text-gray-400">{analyticsCheckActionCopy.helper}</p>
          </div>
        );
      }

      if (decision.key === 'enable-alerts') {
        return (
          <Button
            id={variant === 'primary' ? 'analytics-owner-cta' : undefined}
            size="sm"
            variant={variant === 'primary' ? undefined : variant}
            onClick={() => navigate(analyticsMonitoringHandoff ? getMonitoringHandoffPath(analyticsMonitoringHandoff) : '/alerts')}
          >
            {decision.title}
          </Button>
        );
      }

      if (decision.key === 'open-automation') {
        return (
          <Button
            id={variant === 'primary' ? 'analytics-owner-cta' : undefined}
            size="sm"
            variant={variant === 'primary' ? undefined : variant}
            onClick={() => navigate(analyticsMonitoringHandoff ? getMonitoringHandoffPath(analyticsMonitoringHandoff) : '/automation')}
          >
            {decision.title}
          </Button>
        );
      }

      if (decision.key === 'review-active-rules') {
        return (
          <Button
            id={variant === 'primary' ? 'analytics-owner-cta' : undefined}
            size="sm"
            variant={variant === 'primary' ? undefined : variant}
            onClick={() => navigate('/automation#automation-rules-list')}
          >
            {decision.title}
          </Button>
        );
      }

      return (
        <Button
          id={variant === 'primary' ? 'analytics-owner-cta' : undefined}
          size="sm"
          variant={variant === 'primary' ? undefined : variant}
          onClick={() =>
            navigate(analyticsMonitoringHandoff ? getMonitoringHandoffPath(analyticsMonitoringHandoff) : '/notifications')
          }
        >
          {decision.title}
        </Button>
      );
    },
    [
      analyticsCheckActionCopy.helper,
      analyticsDriftHandoffPath,
      analyticsMonitoringHandoff,
      handleAnalyticsCheckAction,
      navigate,
    ],
  );
  const renderAnalyticsMonitoringRail = useCallback(
    (
      decision: MonitoringLoopDecision | null,
      options?: { winnerKey?: string | null; actionVariant?: 'primary' | 'outline'; className?: string },
    ) => {
      if (!decision) return null;
      if (decision.key === 'open-automation' || decision.key === 'review-active-rules') {
        return null;
      }

      return (
        <InlineStatus
          tone={decision.tone}
          title={decision.title}
          message={decision.message}
          meta={renderMonitoringDecisionMeta(decision)}
          action={
            options?.winnerKey === decision.key
              ? undefined
              : renderAnalyticsMonitoringAction(decision, options?.actionVariant)
          }
          className={`${options?.className ?? ''} rounded-xl`.trim()}
        />
      );
    },
    [
      renderAnalyticsMonitoringAction,
      renderMonitoringDecisionMeta,
    ],
  );
  const renderCoverageSummaryAction = useCallback((
    variant: 'primary' | 'outline' = 'primary',
    suppressActionKinds: string[] = [],
  ) => {
    const action = coverageSummary.action;
    if (!action || suppressActionKinds.includes(action.kind)) return undefined;

    if (action.kind === 'retry-sync' && action.connectionId) {
      const staleMatch = staleConnections.find((stale) => stale.connectionId === action.connectionId);
      return (
        <Button
          id={variant === 'primary' ? 'analytics-owner-cta' : undefined}
          size="sm"
          variant={variant === 'primary' ? undefined : variant}
          onClick={() => handleSyncStale(
            staleMatch ?? {
              connectionId: action.connectionId!,
              exchange: action.exchange ?? 'exchange',
              last_synced_at: null,
              isStale: false,
              staleHours: null,
            },
          )}
          isLoading={syncingStaleId === action.connectionId}
          loadingLabel="Syncing…"
        >
          {action.label}
        </Button>
      );
    }

    if (action.kind === 'review-source' || action.kind === 'review-coverage') {
      return (
        <Button
          id={variant === 'primary' ? 'analytics-owner-cta' : undefined}
          size="sm"
          variant={variant === 'primary' ? undefined : variant}
          onClick={() => navigate(action.href)}
        >
          {action.label}
        </Button>
      );
    }

    if (action.kind === 'review-portfolio') {
      return renderAnalyticsPortfolioReviewAction(variant);
    }

    if (action.kind === 'review-import') {
      return (
        <Button
          id={variant === 'primary' ? 'analytics-owner-cta' : undefined}
          size="sm"
          variant={variant === 'primary' ? undefined : variant}
          onClick={() => navigate('/import')}
        >
          {action.label}
        </Button>
      );
    }

    if (action.kind === 'review-wallets') {
      return (
        <Button
          id={variant === 'primary' ? 'analytics-owner-cta' : undefined}
          size="sm"
          variant={variant === 'primary' ? undefined : variant}
          onClick={() => navigate('/wallet-hub')}
        >
          {action.label}
        </Button>
      );
    }

    if (action.kind === 'manage-exchange') {
      return (
        <Button
          id={variant === 'primary' ? 'analytics-owner-cta' : undefined}
          size="sm"
          variant={variant === 'primary' ? undefined : variant}
          onClick={() => navigate('/exchange-sync')}
        >
          {action.label}
        </Button>
      );
    }

    return undefined;
  }, [coverageSummary.action, handleSyncStale, navigate, renderAnalyticsPortfolioReviewAction, staleConnections, syncingStaleId]);
  const renderAnalyticsArrivalAction = useCallback((variant: 'primary' | 'outline' = 'primary') => {
    if (!analyticsArrivalActionDecision) return undefined;

    if (analyticsArrivalActionDecision.source === 'coverage') {
      if (analyticsArrivalActionDecision.key === 'review-portfolio') {
        return renderAnalyticsPortfolioReviewAction(variant);
      }

      return renderCoverageSummaryAction(variant);
    }

    return renderAnalyticsMonitoringAction(analyticsMonitoringDecision!, variant);
  }, [
    analyticsArrivalActionDecision,
    analyticsMonitoringDecision,
    renderAnalyticsMonitoringAction,
    renderAnalyticsPortfolioReviewAction,
    renderCoverageSummaryAction,
  ]);
  const analyticsCarriedProof = useMemo(() => {
    if (showNoPortfolioState || !hasLoadedPortfolioValue || !analyticsArrivalActionDecision) {
      return null;
    }

    if (analyticsArrivalActionDecision.source === 'coverage') {
      return (
        <div data-testid="analytics-carried-proof">
          <PortfolioCoverageSummaryRail
            summary={coverageSummary}
            action={renderCoverageSummaryAction(
              'outline',
              coverageSummary.action &&
                (
                  (
                    coverageSummary.action.label === 'Review Portfolio'
                  ) ||
                  (
                    analyticsMonitoringDecision?.key === 'check-analytics' &&
                    coverageSummary.action.kind === 'review-portfolio'
                  )
                )
                ? [coverageSummary.action.kind]
                : [],
            )}
            className="truth-support-status"
          />
        </div>
      );
    }

    if (!analyticsMonitoringDecision) {
      return null;
    }

    return (
      <div data-testid="analytics-carried-proof">
        {renderAnalyticsMonitoringRail(analyticsMonitoringDecision, {
          winnerKey: analyticsArrivalActionDecision.key,
          actionVariant: 'outline',
          className: 'truth-support-status',
        })}
      </div>
    );
  }, [
    analyticsArrivalActionDecision,
    analyticsMonitoringDecision,
    coverageSummary,
    hasLoadedPortfolioValue,
    renderAnalyticsMonitoringRail,
    renderCoverageSummaryAction,
    showNoPortfolioState,
  ]);
  const renderAutomationCoverageAction = useCallback(
    (variant: 'primary' | 'outline' = 'primary') => {
      if (!automationCoverageSummary.action) {
        return undefined;
      }

      const actionPath =
        automationCoverageSummary.action.key === 'open-automation'
          ? analyticsMonitoringHandoff
            ? getMonitoringHandoffPath(analyticsMonitoringHandoff)
            : '/automation'
          : '/automation#automation-rules-list';

      return (
        <Button
          id={variant === 'primary' ? 'analytics-owner-cta' : undefined}
          size="sm"
          variant={variant === 'primary' ? undefined : variant}
          onClick={() => navigate(actionPath)}
        >
          {automationCoverageSummary.action.label}
        </Button>
      );
    },
    [analyticsMonitoringHandoff, automationCoverageSummary.action, navigate],
  );
  const analyticsOutcomeFollowUp = useMemo(() => {
    if (!analyticsArrivalActionDecision) {
      return 'Check Analytics';
    }

    if (analyticsArrivalActionDecision.source === 'coverage') {
      if (coverageSummary.action?.kind === 'review-portfolio') {
        return 'Check Analytics';
      }

      return coverageSummary.action?.label ?? 'Check Analytics';
    }

    if (!analyticsMonitoringDecision) {
      return 'Check Analytics';
    }

    if (
      analyticsMonitoringDecision.key === 'open-automation' ||
      analyticsMonitoringDecision.key === 'review-active-rules'
    ) {
      return 'Check Analytics';
    }

    return analyticsMonitoringDecision.title;
  }, [
    analyticsArrivalActionDecision,
    analyticsMonitoringDecision,
    coverageSummary.action,
  ]);
  const analyticsProofLabel = portfolioSummary
    ? `${formatValue(portfolioSummary.totalValueUsd, { decimals: 2 })} analytics value proof`
    : null;
  useEffect(() => {
    if (!user?.id || !automationOutcomeHandoff) {
      return;
    }

    writePersistedAutomationOutcomeHandoff('portfolio-analytics', user.id, automationOutcomeHandoff);
  }, [automationOutcomeHandoff, user?.id]);

  useEffect(() => {
    if (!hasLoadedPortfolioValue || !analyticsOutcomeFollowUp || !analyticsProofLabel) {
      return;
    }

    captureAuthReturnContext({
      pathname: location.pathname,
      search: location.search,
      ownerCtaLabel: analyticsOutcomeFollowUp,
      proofLabel: analyticsProofLabel,
      focus: 'analytics-owner-cta',
    });
  }, [
    analyticsOutcomeFollowUp,
    analyticsProofLabel,
    hasLoadedPortfolioValue,
    location.pathname,
    location.search,
  ]);

  useEffect(() => {
    if (authRecoveryState?.focus !== 'analytics-owner-cta') {
      return;
    }

    const target = document.getElementById('analytics-owner-cta');
    if (!target) {
      return;
    }

    target.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    target.focus();

    const nextSearch = stripAuthRecoveryParams(location.search);
    navigate(`${location.pathname}${nextSearch}`, { replace: true });
  }, [authRecoveryState?.focus, location.pathname, location.search, navigate]);
  const benchmarkConsequenceSummary = useMemo(
    () =>
      buildBenchmarkConsequenceSummary({
        activeRangeContext: displayedRangeContext,
        historyCount: history?.snapshots.length ?? 0,
        twrValue,
        btcChangePct,
        ethChangePct,
        benchmarkGeneratedAt: benchmarks?.generatedAt ?? null,
        followUpLabel: analyticsOutcomeFollowUp,
      }),
    [
      analyticsOutcomeFollowUp,
      benchmarks?.generatedAt,
      btcChangePct,
      displayedRangeContext,
      ethChangePct,
      history?.snapshots.length,
      twrValue,
    ],
  );
  const analyticsProofHelper = useMemo(() => {
    if (!hasAnalyticsData) {
      return null;
    }

    if (authRefreshState.status === 'sign_in_required') {
      return {
        tone: 'warning' as const,
        eyebrow: 'AUTH_SESSION_BLOCKED',
        message:
          `${AUTH_SESSION_BLOCKED_MESSAGE}. The mounted analytics proof stack remains the last truthful answer on /portfolio-analytics.`,
        meta: (
          <>
            <span>
              Blocker: <span className="setup-trust-helper__code">AUTH_SESSION_BLOCKED</span>
            </span>
            <span>
              Session:{' '}
              <span className="setup-trust-helper__code">
                {authRefreshState.message ?? 'Session expired. Please sign in again.'}
              </span>
            </span>
          </>
        ),
      };
    }

    if (authRefreshState.status === 'retrying' || authRefreshState.status === 'degraded') {
      return {
        tone: authRefreshState.status === 'degraded' ? 'warning' as const : 'info' as const,
        eyebrow: 'Keep working from mounted proof',
        message:
          'Pinecurl is still validating the owner session locally. The mounted analytics proof stack remains the last truthful answer while fresh range data reloads.',
        meta: (
          <>
            <span>
              Route state: <span className="setup-trust-helper__code">{authRefreshState.status}</span>
            </span>
            {authRefreshState.message ? (
              <span>
                Session: <span className="setup-trust-helper__code">{authRefreshState.message}</span>
              </span>
            ) : null}
          </>
        ),
      };
    }

    return null;
  }, [authRefreshState.message, authRefreshState.status, hasAnalyticsData]);
  const analyticsFirstValueFallback = useMemo(() => {
    if (setupHandoff?.kind === 'csv-import') {
      return {
        tone: 'info' as const,
        title: `${setupHandoff.sourceLabel} still owns the first portfolio value step`,
        message:
          'Portfolio Analytics is downstream from /import. Go back to /import to confirm the mounted import proof before treating analytics as the first-value surface.',
        primaryLabel: 'Back to Import',
        primaryPath: '/import',
        secondaryLabel: 'Back to Wallet Hub',
        secondaryPath: '/wallet-hub',
        meta: (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
            <span>
              Source:{' '}
              <span className="font-mono tabular-nums text-gray-200">{setupHandoff.sourceLabel}</span>
            </span>
            <span>
              Proof time:{' '}
              <span className="font-mono tabular-nums text-gray-200">{formatSetupHandoffTimestamp(setupHandoff.occurredAt)}</span>
            </span>
            <span>
              Imported · Skipped · Errors:{' '}
              <span className="font-mono tabular-nums text-gray-200">
                {setupHandoff.imported} · {setupHandoff.skipped} · {setupHandoff.errors}
              </span>
            </span>
          </div>
        ),
        helper: {
          eyebrow: 'Analytics follows first value; it does not create it',
          message:
            'Keep the mounted /import proof as the owner. Use the single filled CTA below to return there, and come back here only after first portfolio value is visible.',
        },
      };
    }

    if (setupHandoff?.kind === 'api-sync') {
      return {
        tone: 'info' as const,
        title: `${setupHandoff.exchangeLabel} sync still owns the first portfolio value step`,
        message:
          'Analytics is still waiting for first portfolio value from the mounted exchange proof. Go back to /import to review sync freshness or blocker detail before expecting deeper analytics here.',
        primaryLabel: 'Back to Import',
        primaryPath: '/import',
        secondaryLabel: 'Back to Wallet Hub',
        secondaryPath: '/wallet-hub',
        meta: (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
            <span>
              Source:{' '}
              <span className="font-mono tabular-nums text-gray-200">{setupHandoff.exchangeLabel}</span>
            </span>
            <span>
              Proof time:{' '}
              <span className="font-mono tabular-nums text-gray-200">{formatSetupHandoffTimestamp(setupHandoff.occurredAt)}</span>
            </span>
            <span>
              Imported · Skipped · Errors:{' '}
              <span className="font-mono tabular-nums text-gray-200">
                {setupHandoff.imported} · {setupHandoff.skipped} · {setupHandoff.errors}
              </span>
            </span>
          </div>
        ),
        helper: {
          eyebrow: 'Keep the sync owner in /import',
          message:
            'Wallet Hub stays optional for extra on-chain coverage, but /import remains the truthful owner of this exchange proof until analytics can read first portfolio value.',
        },
      };
    }

    if (
      coverageSummary.action?.kind === 'review-import' ||
      coverageSummary.action?.kind === 'retry-sync' ||
      coverageSummary.action?.kind === 'manage-exchange'
    ) {
      return {
        tone: 'info' as const,
        title: 'Analytics is waiting on /import proof',
        message:
          'This route should deepen an already-proven portfolio value, not replace source proof. Open /import to finish the current source-owned step first.',
        primaryLabel: 'Open Import',
        primaryPath: '/import',
        secondaryLabel: 'Open Wallet Hub',
        secondaryPath: '/wallet-hub',
        meta: null,
        helper: {
          eyebrow: 'One owner route at a time',
          message:
            'Keep Wallet Hub for wallet setup and /import for CSV or exchange proof. Portfolio Analytics should stay a downstream review surface.',
        },
      };
    }

    return {
      tone: 'info' as const,
      title: 'First portfolio value is not proven yet',
      message:
        'Start in Wallet Hub when the first proof comes from an on-chain wallet, or use /import when the first proof comes from CSV or exchange sync.',
      primaryLabel: 'Open Wallet Hub',
      primaryPath: '/wallet-hub',
      secondaryLabel: 'Open Import',
      secondaryPath: '/import',
      meta: null,
      helper: {
        eyebrow: 'Choose one owner route first',
        message:
          'Portfolio Analytics should only take over after Wallet Hub or /import already proved the source and first value.',
      },
    };
  }, [coverageSummary.action, setupHandoff]);
  const renderAnalyticsFirstValueLoopCard = useCallback(() => (
    <div className="space-y-4" data-testid="analytics-first-value-loop">
      <InlineStatus
        tone={analyticsFirstValueFallback.tone}
        title={analyticsFirstValueFallback.title}
        message={analyticsFirstValueFallback.message}
        meta={analyticsFirstValueFallback.meta ?? undefined}
        className="rounded-xl"
      />
      <SetupTrustHelperBand
        tone="info"
        eyebrow={analyticsFirstValueFallback.helper.eyebrow}
        message={analyticsFirstValueFallback.helper.message}
      />
      <div className="flex flex-wrap gap-3">
        <Button size="sm" onClick={() => navigate(analyticsFirstValueFallback.primaryPath)}>
          {analyticsFirstValueFallback.primaryLabel}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => navigate(analyticsFirstValueFallback.secondaryPath)}
        >
          {analyticsFirstValueFallback.secondaryLabel}
        </Button>
      </div>
    </div>
  ), [analyticsFirstValueFallback, navigate]);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-white">Portfolio Analytics</h1>
            <LiveIndicator connected={priceStreamConnected} />
          </div>
          <p className="text-sm text-gray-400 mt-1">Deep-dive performance &amp; risk metrics</p>
        </div>
      </div>

      {isRefreshing && hasAnalyticsData ? (
        <InlineStatus
          tone="info"
          title={displayedRangeContext === activeRangeContext ? 'Refreshing analytics' : 'Loading selected range'}
          message={
            displayedRangeContext === activeRangeContext
              ? `Rechecking the ${activeRangeContext} analytics view while your last successful data stays mounted.`
              : `Showing the last successful ${displayedRangeContext} analytics snapshot while the ${activeRangeContext} view refreshes.`
          }
          meta={(
            <span className="font-mono tabular-nums">
              {activeRangeContext}
              {displayedAtLabel ? ` · last good ${displayedAtLabel}` : ''}
            </span>
          )}
          compact
        />
      ) : null}

      {/* Error state */}
      {error && !hasAnalyticsData && (
        <InlineStatus
          tone={analyticsErrorTone}
          title={
            analyticsErrorTone === 'error'
              ? 'Analytics unavailable'
              : 'Analytics partially unavailable'
          }
          message={error}
          action={<Button onClick={refetch} size="sm">Try again</Button>}
        />
      )}

      <section data-testid="analytics-owner-stack" className="space-y-4">
        {hasLoadedPortfolioValue ? (
          <PortfolioValueArrivalSummary
            valueLabel={formatValue(portfolioSummary!.totalValueUsd, { decimals: 2 })}
            summary={coverageSummary}
            emphasis="calm"
            className="border-blue-500/20 bg-blue-500/5"
            testId="analytics-arrival-summary"
          />
        ) : null}
        {hasLoadedPortfolioValue && analyticsArrivalActionDecision ? (
          <div className="decision-owner-stack space-y-4" data-testid="analytics-owner-summary">
            <div className="space-y-2">
              <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-blue-200/80">Owner summary</p>
              <h2 className="text-2xl font-semibold text-white">{analyticsOutcomeFollowUp}</h2>
              <p className="text-sm text-blue-100/90">
                {analyticsArrivalActionDecision.source === 'coverage'
                  ? `${coverageSummary.title} already proves the loaded analytics context, so ${analyticsOutcomeFollowUp} stays the one next step before benchmark, export, or range controls compete for attention.`
                  : `${analyticsMonitoringDecision?.title ?? 'Monitoring follow-up'} stays attached to the mounted analytics proof, so the same next step remains dominant while benchmark and export detail stay supporting-only below.`}
              </p>
            </div>
            <div className="decision-owner-stack__proof-list">
              <div className="decision-owner-stack__proof-chip">
                Owner route:{' '}
                <span className="decision-owner-stack__proof-value">Portfolio Analytics</span>
              </div>
              <div className="decision-owner-stack__proof-chip">
                Winning proof:{' '}
                <span className="decision-owner-stack__proof-value">
                  {analyticsArrivalActionDecision.source === 'coverage' ? coverageSummary.title : analyticsMonitoringDecision?.title ?? 'Check Analytics'}
                </span>
              </div>
              <div className="decision-owner-stack__proof-chip">
                Range:{' '}
                <span className="decision-owner-stack__proof-value">{displayedRangeContext}</span>
              </div>
              {displayedAtLabel ? (
                <div className="decision-owner-stack__proof-chip">
                  Last good:{' '}
                  <span className="decision-owner-stack__proof-value">{displayedAtLabel}</span>
                </div>
              ) : null}
            </div>
            <div className="decision-owner-stack__actions">
              {renderAnalyticsArrivalAction('primary')}
              <div className="rounded-xl border border-blue-400/20 bg-gray-950/40 px-4 py-3 lg:min-w-[18rem]">
                <p className="text-[11px] uppercase tracking-[0.18em] text-blue-200/80">Supporting surfaces</p>
                <p className="mt-2 text-sm text-white">Benchmark consequence, export, and range controls stay available below.</p>
              </div>
            </div>
          </div>
        ) : null}
        {analyticsProofHelper ? (
          <SetupTrustHelperBand
            tone={analyticsProofHelper.tone}
            eyebrow={analyticsProofHelper.eyebrow}
            message={analyticsProofHelper.message}
            meta={analyticsProofHelper.meta}
            className="mt-0"
          />
        ) : error && hasAnalyticsData ? (
          <InlineStatus
            tone="warning"
            title="Retry later"
            message="The latest analytics refresh could not complete. Pinecurl keeps the mounted proof stack visible while route-owned chart and export requests recover."
            meta={(
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {displayedAtLabel ? (
                  <span className="font-mono tabular-nums">
                    {displayedRangeContext} · last good {displayedAtLabel}
                  </span>
                ) : null}
                <span className="font-mono tabular-nums">{error}</span>
              </div>
            )}
            action={<Button variant="outline" onClick={refetch} size="sm">Try again</Button>}
            className="rounded-xl"
            compact
          />
        ) : null}
        {analyticsCarriedProof}
        {benchmarkConsequenceSummary ? (
          <div data-testid="analytics-benchmark-consequence">
            <InlineStatus
              tone={benchmarkConsequenceSummary.tone}
              title={benchmarkConsequenceSummary.title}
              message={benchmarkConsequenceSummary.message}
              meta={(
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                  {benchmarkConsequenceSummary.proof.map((item) => (
                    <span key={item.label}>
                      {item.label}:{' '}
                      <span className="font-mono tabular-nums text-gray-200">{item.value}</span>
                    </span>
                  ))}
                </div>
              )}
              className="decision-support-panel rounded-xl"
              compact
            />
          </div>
        ) : null}
        {!showNoPortfolioState && automationOutcomeHandoff ? (
          <AutomationOutcomeRail
            handoff={automationOutcomeHandoff}
            nextManualFollowUp={analyticsOutcomeFollowUp}
            title={
              automationOutcomeHandoff.outcomeState === 'matched'
                ? 'Matched automation proof returned to analytics'
                : automationOutcomeHandoff.outcomeState === 'unmatched'
                  ? 'Watching automation context returned to analytics'
                  : 'No-execution automation context returned to analytics'
            }
            message={
              automationOutcomeHandoff.outcomeState === 'matched'
                ? 'Automation recorded this advisory match on /automation. Portfolio Analytics keeps the carried proof inside the first analytics cluster while the route-owned review stays dominant.'
                : automationOutcomeHandoff.outcomeState === 'unmatched'
                  ? 'Automation is still watching this saved rule on /automation. Portfolio Analytics keeps that waiting state explicit without letting it read like missing analytics data.'
                  : 'Automation has not recorded an execution for this saved rule on /automation yet. Portfolio Analytics keeps the owner review visible without inflating urgency.'
            }
            pinecurlAction={
              automationOutcomeHandoff.outcomeState === 'matched'
                ? 'Pinecurl already surfaced this monitoring outcome for deeper analytics review.'
                : automationOutcomeHandoff.outcomeState === 'unmatched'
                  ? 'Pinecurl is still watching this saved rule before surfacing a deeper analytics match.'
                  : 'Pinecurl has not recorded a deeper analytics execution for this saved rule yet.'
            }
            helper="The source route, proof time, and current analytics-owned follow-up stay visible together without replacing the route’s existing trust rails."
            className="truth-support-status"
            testId="analytics-automation-outcome-rail"
          />
        ) : null}
        <div
          className="decision-utility-band rounded-xl border border-gray-800/80 bg-gray-950/50 p-4 md:p-5"
          data-testid="analytics-support-utilities"
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-2xl space-y-1.5">
              <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-gray-500">
                Supporting utilities
              </p>
              <p className="text-sm text-gray-300">
                {hasLoadedPortfolioValue && analyticsArrivalActionDecision
                  ? `${analyticsOutcomeFollowUp} stays the route-owned next review. Export and range controls stay available here when you need supporting proof.`
                  : 'Export and range controls stay available here whenever you need supporting proof for this analytics view.'}
              </p>
            </div>
            <div className="flex w-full flex-col gap-3 lg:w-auto lg:min-w-[20rem] lg:items-end">
              <Button
                onClick={() => { void handleExportPdf(); }}
                variant="outline"
                size="sm"
                isLoading={pdfLoading}
                loadingLabel="Generating PDF…"
                aria-label="Export portfolio as PDF"
                disabled={!user?.id || showNoPortfolioState}
                disabledReason={showNoPortfolioState ? 'Connect portfolio data before exporting analytics.' : undefined}
              >
                Export PDF
              </Button>
              {pdfStatus ? (
                <div className="w-full max-w-xl">
                  <InlineStatus
                    tone={pdfStatus.tone}
                    title={pdfStatus.title}
                    message={pdfStatus.message}
                    meta={pdfStatus.meta ? <span className="font-mono tabular-nums">{pdfStatus.meta}</span> : undefined}
                    compact
                    action={pdfStatus.tone === 'error' ? (
                      <Button onClick={() => { void handleExportPdf(); }} size="sm" variant="outline">
                        Retry Export
                      </Button>
                    ) : undefined}
                  />
                </div>
              ) : null}
              <div className="flex flex-col gap-2 lg:items-end">
                <div className="flex flex-wrap items-center gap-2">
                  {rangeMode === 'preset' ? (
                    <TimeRangeSelector value={range} onChange={setRange} />
                  ) : (
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="date"
                        value={customFromStr}
                        max={customToStr}
                        onChange={(e) => setCustomFromStr(e.target.value)}
                        className="rounded-lg border border-gray-700 bg-gray-800 px-2 py-1 text-sm text-white focus:border-blue-500 focus:outline-none"
                        aria-label="Custom range start date"
                      />
                      <span className="text-sm text-gray-500">→</span>
                      <input
                        type="date"
                        value={customToStr}
                        min={customFromStr}
                        max={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => setCustomToStr(e.target.value)}
                        className="rounded-lg border border-gray-700 bg-gray-800 px-2 py-1 text-sm text-white focus:border-blue-500 focus:outline-none"
                        aria-label="Custom range end date"
                      />
                    </div>
                  )}
                  <button
                    onClick={() => setRangeMode(rangeMode === 'preset' ? 'custom' : 'preset')}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                      rangeMode === 'custom'
                        ? 'border-blue-500/60 bg-blue-500/10 text-blue-300'
                        : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-500'
                    }`}
                    title={rangeMode === 'preset' ? 'Switch to custom date range' : 'Switch to preset range'}
                  >
                    {rangeMode === 'preset' ? 'Custom' : 'Preset'}
                  </button>
                </div>
                {showNoPortfolioState ? (
                  <p className="max-w-md text-xs text-gray-400 lg:text-right">
                    Export becomes available after first portfolio value is mounted.{' '}
                    <Link to={analyticsFirstValueFallback.primaryPath} className="text-blue-400 hover:text-blue-300">
                      {analyticsFirstValueFallback.primaryLabel}
                    </Link>
                    .
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>

      {(!hasLoadedPortfolioValue || !analyticsArrivalActionDecision) ? (
        <PortfolioCoverageSummaryRail
          summary={coverageSummary}
          action={renderCoverageSummaryAction(
            showNoPortfolioState ? 'outline' : 'primary',
            coverageSummary.action &&
              analyticsMonitoringDecision?.key === 'check-analytics' &&
              coverageSummary.action.kind === 'review-portfolio'
              ? [coverageSummary.action.kind]
              : [],
          )}
        />
      ) : null}

      {!showNoPortfolioState && (!hasLoadedPortfolioValue || !analyticsArrivalActionDecision)
        ? renderAnalyticsMonitoringRail(analyticsMonitoringDecision, {
            winnerKey: analyticsMonitoringDecision?.key === 'check-analytics' ? analyticsMonitoringDecision.key : null,
            actionVariant: 'primary',
          })
        : null}
      {!showNoPortfolioState ? (
        <AutomationCoverageRail
          summary={automationCoverageSummary}
          action={renderAutomationCoverageAction(
            hasLoadedPortfolioValue && analyticsArrivalActionDecision ? 'outline' : 'primary',
          )}
          className={hasLoadedPortfolioValue && analyticsArrivalActionDecision ? 'truth-support-status' : undefined}
          testId="analytics-automation-coverage"
        />
      ) : null}

      {showNoPortfolioState && (
        <div className="space-y-4">
          {renderAnalyticsMonitoringRail(analyticsMonitoringDecision, { actionVariant: 'outline' })}
          {topExchangeProofGap ? (
            <InlineStatus
              tone={topExchangeProofGap.tone}
              title={topExchangeProofGap.title}
              message={topExchangeProofGap.message}
              meta={<span className="font-mono tabular-nums">{topExchangeProofGap.meta}</span>}
              action={(
                <Button size="sm" variant="outline" onClick={() => navigate('/import')}>
                  Back to Import
                </Button>
              )}
              className="rounded-xl"
            />
          ) : null}
          {renderAnalyticsFirstValueLoopCard()}
        </div>
      )}

      {/* Exchange staleness banner (Chunk 92) */}
      {lingeringExchangeStatuses.length > 0 && (
        <div className="space-y-3">
          {lingeringExchangeStatuses.map(([connectionId, status]) => (
            <InlineStatus
              key={connectionId}
              tone={status.tone}
              title={status.title}
              message={status.message}
              meta={<span className="font-mono tabular-nums">{activeRangeContext}</span>}
              compact
            />
          ))}
        </div>
      )}
      {staleConnections.map((stale) => (
        <InlineStatus
          key={stale.connectionId}
          tone={exchangeSyncStatus[stale.connectionId]?.tone === 'error' ? 'error' : 'warning'}
          title={exchangeSyncStatus[stale.connectionId]?.title ?? `${stale.exchange} data is stale`}
          message={
            exchangeSyncStatus[stale.connectionId]?.tone === 'error'
              ? exchangeSyncStatus[stale.connectionId]?.message ?? ''
              : exchangeSyncStatus[stale.connectionId]?.message ??
                `${stale.exchange} balances are ${stale.staleHours !== null
                  ? stale.staleHours >= 48
                    ? `${Math.floor(stale.staleHours / 24)} days`
                    : `${stale.staleHours}h`
                  : 'an unknown amount of time'} old, so analytics may be inaccurate until a fresh sync completes.`
          }
          meta={<span className="font-mono tabular-nums">{activeRangeContext} · {stale.staleHours !== null ? `${stale.staleHours}h` : 'unknown'}</span>}
          compact
          action={(
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleSyncStale(stale)}
              isLoading={syncingStaleId === stale.connectionId}
              loadingLabel="Syncing…"
              disabledReason={syncingStaleId === stale.connectionId ? `Sync already running for ${stale.exchange}.` : undefined}
            >
              Sync Now
            </Button>
          )}
        />
      ))}
      {exchangeProofGaps.map(({ connection, proof }) => (
        <InlineStatus
          key={`gap-${connection.id}`}
          tone={proof.tone}
          title={proof.title}
          message={proof.message}
          meta={<span className="font-mono tabular-nums">{proof.meta}</span>}
          compact
          action={(
            <Button size="sm" variant="outline" onClick={() => navigate('/import')}>
              Back to Import
            </Button>
          )}
        />
      ))}

      {!showNoPortfolioState && (
        <>
      {/* Top metrics row */}
      <div
        ref={analyticsEvidenceRef}
        tabIndex={-1}
        className="grid grid-cols-2 gap-4 outline-none md:grid-cols-4"
      >
        <MetricCard
          label="Time-Weighted Return"
          value={twrValue}
          isLoading={showInitialAnalyticsLoad}
          colorClass={(twrValue ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}
        />
        <MetricCard
          label="Money-Weighted Return"
          value={mwrValue}
          isLoading={showInitialAnalyticsLoad}
          colorClass={(mwrValue ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}
        />
        <MetricCard
          label="Annualized Volatility"
          value={annualizedVol}
          isLoading={showInitialAnalyticsLoad}
          colorClass="text-amber-400"
        />
        <MetricCard
          label="Total Return"
          value={totalReturnValue}
          isLoading={showInitialAnalyticsLoad}
          colorClass={(totalReturnValue ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}
        />
      </div>

      {/* Risk-adjusted metrics panel (Sharpe, Sortino, Max Drawdown, Calmar) */}
      <RiskMetricsPanel defaultRange={rangeMode === 'custom' || range === '7d' || range === 'all' ? '30d' : range} />

      {/* Custom range summary cards — shown when custom range active (Chunk 158) */}
      {rangeMode === 'custom' && performance && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <p className="text-xs text-gray-400 mb-1">Start Value</p>
            <p className="text-xl font-bold font-mono tabular-nums text-white">
              {formatValue(performance.startValue)}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">{customFromStr}</p>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <p className="text-xs text-gray-400 mb-1">End Value</p>
            <p className="text-xl font-bold font-mono tabular-nums text-white">
              {formatValue(performance.endValue)}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">{customToStr}</p>
          </div>
        </div>
      )}

      {/* Portfolio value over time chart */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 md:p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Portfolio vs BTC &amp; ETH</h2>
        {showInitialAnalyticsLoad ? (
          <div className="skeleton h-48 w-full rounded-lg" />
        ) : history?.snapshots && history.snapshots.length > 0 ? (
          <PerformanceChart
            snapshots={history.snapshots}
            btcChangePct={btcChangePct}
            ethChangePct={ethChangePct}
            formatValue={formatValue}
          />
        ) : (
          <InlineStatus
            tone={
              staleConnections.length > 0
                ? 'warning'
                : topExchangeProofGap
                  ? topExchangeProofGap.tone
                : analyticsMonitoringDecision
                    ? analyticsMonitoringDecision.tone
                    : 'info'
            }
            title={
              staleConnections.length > 0
                ? 'History may be waiting on fresh balances'
                : topExchangeProofGap?.title
                  ?? analyticsMonitoringDecision?.title
                  ?? 'No history for this period'
            }
            message={
              staleConnections.length > 0
                ? `The selected ${activeRangeContext} view does not have fresh snapshots yet because one or more upstream balances are stale.`
                : topExchangeProofGap?.message ?? analyticsMonitoringDecision?.message ?? `The selected ${activeRangeContext} period does not include any saved portfolio history yet.`
            }
            meta={
              topExchangeProofGap && !staleConnections.length ? (
                <span className="font-mono tabular-nums">{topExchangeProofGap.meta}</span>
              ) : analyticsMonitoringDecision && !staleConnections.length ? (
                renderMonitoringDecisionMeta(analyticsMonitoringDecision)
              ) : (
                <span className="font-mono tabular-nums">{activeRangeContext}</span>
              )
            }
            action={staleConnections.length > 0 ? (
              <Button onClick={() => handleSyncStale(staleConnections[0])} size="sm" disabled={syncingStaleId === staleConnections[0]?.connectionId}>
                Sync recent data
              </Button>
            ) : topExchangeProofGap ? (
              <Button onClick={() => navigate('/import')} size="sm">
                Back to Import
              </Button>
            ) : analyticsMonitoringDecision && analyticsMonitoringDecision.key !== 'check-analytics' ? (
              renderAnalyticsMonitoringAction(analyticsMonitoringDecision)
            ) : canBroadenRange ? (
              <Button onClick={broadenRange} size="sm">
                Choose a broader range
              </Button>
            ) : undefined}
          />
        )}
      </div>

      {/* Benchmark Comparison (Chunk 83 + Chunk 158: custom range + S&P 500) */}
      <BenchmarkComparisonChart
        from={rangeMode === 'custom' ? customFromStr : undefined}
        to={rangeMode === 'custom' ? customToStr : undefined}
      />

      {/* Volatility metrics */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 md:p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Volatility Metrics</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-400 mb-1">30-Day Rolling</p>
            <p className="text-xl font-mono tabular-nums text-amber-400">
              {volatility?.rolling30Day != null ? `${volatility.rolling30Day.toFixed(2)}%` : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-1">Annualized</p>
            <p className="text-xl font-mono tabular-nums text-amber-400">
              {volatility?.annualized != null ? `${volatility.annualized.toFixed(2)}%` : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-1">Daily</p>
            <p className="text-xl font-mono tabular-nums text-gray-300">
              {volatility?.daily != null ? `${volatility.daily.toFixed(2)}%` : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-1">Trend</p>
            <p className="text-sm text-gray-300 capitalize">{volatility?.trend ?? '—'}</p>
          </div>
        </div>
      </div>

      {/* Return attribution */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 md:p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Return Attribution</h2>
        {showInitialAnalyticsLoad ? (
          <div className="skeleton h-32 w-full" />
        ) : sortedByReturn.length > 0 ? (
          <div className="space-y-2">
            {sortedByReturn.map((a) => {
              const annotation = annotations[a.symbol];
              const statusEmoji =
                annotation?.thesisStatus === 'bullish' ? '🐂' :
                annotation?.thesisStatus === 'bearish' ? '🐻' :
                annotation?.thesisStatus === 'neutral' ? '➖' : null;
              const statusColor =
                annotation?.thesisStatus === 'bullish' ? 'text-emerald-400' :
                annotation?.thesisStatus === 'bearish' ? 'text-red-400' :
                'text-gray-500';
              return (
                <div key={a.symbol} className="flex items-center gap-3">
                  <span className="text-sm text-gray-300 w-16 font-mono">{a.symbol}</span>
                  {statusEmoji && (
                    <span className={`text-xs font-medium ${statusColor}`} title={annotation?.thesisStatus}>
                      {statusEmoji}
                    </span>
                  )}
                  <div className="flex-1 bg-gray-800 rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-2 rounded-full ${a.return >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`}
                      style={{
                        width: `${Math.min(Math.abs((a.return / maxAbsReturn) * 100), 100)}%`,
                      }}
                    />
                  </div>
                  <span
                    className={`text-sm font-mono tabular-nums w-16 text-right ${
                      a.return >= 0 ? 'text-emerald-400' : 'text-red-400'
                    }`}
                  >
                    {a.return >= 0 ? '+' : ''}
                    {a.return.toFixed(2)}%
                  </span>
                  <button
                    onClick={() => openAnnotationPanel(a.symbol)}
                    className="text-gray-500 hover:text-indigo-400 transition-colors p-1 rounded"
                    aria-label={`Add thesis for ${a.symbol}`}
                    title="Add thesis"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-500 text-sm">No attribution data available</p>
        )}
      </div>

      {/* Annotation slide-out panel (Chunk 141) */}
      {annotationPanelSymbol && (
        <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label={`Investment thesis for ${annotationPanelSymbol}`}>
          <div className="absolute inset-0 bg-black/50" onClick={closeAnnotationPanel} aria-hidden="true" />
          <div className="relative w-full max-w-md bg-gray-900 border-l border-gray-700 h-full overflow-y-auto p-6 flex flex-col gap-5 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                Investment Thesis — {annotationPanelSymbol}
              </h3>
              <button onClick={closeAnnotationPanel} className="text-gray-400 hover:text-white transition-colors" aria-label="Close panel">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Thesis status toggle */}
            <div>
              <p className="text-xs text-gray-400 mb-2 font-medium uppercase tracking-wide">Thesis Status</p>
              <div className="flex gap-2">
                {(['bullish', 'neutral', 'bearish'] as ThesisStatus[]).map((status) => {
                  const emoji = status === 'bullish' ? '🐂' : status === 'bearish' ? '🐻' : '➖';
                  const label = status.charAt(0).toUpperCase() + status.slice(1);
                  const activeClass =
                    annotationDraft.thesisStatus === status
                      ? status === 'bullish'
                        ? 'bg-emerald-600/30 border-emerald-500 text-emerald-400'
                        : status === 'bearish'
                        ? 'bg-red-600/30 border-red-500 text-red-400'
                        : 'bg-gray-700 border-gray-500 text-gray-300'
                      : 'border-gray-700 text-gray-500 hover:border-gray-500';
                  return (
                    <button
                      key={status}
                      onClick={() => setAnnotationDraft((d) => ({ ...d, thesisStatus: status }))}
                      className={`flex-1 flex items-center justify-center gap-1.5 border rounded-lg px-3 py-2 text-sm font-medium transition-colors ${activeClass}`}
                    >
                      <span>{emoji}</span>
                      <span>{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Personal target price */}
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 font-medium uppercase tracking-wide" htmlFor="target-price">
                Personal Target Price (USD)
              </label>
              <input
                id="target-price"
                type="number"
                min="0"
                step="any"
                placeholder="e.g. 120000"
                value={annotationDraft.personalTargetPrice}
                onChange={(e) => setAnnotationDraft((d) => ({ ...d, personalTargetPrice: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500 placeholder-gray-600"
              />
            </div>

            {/* Thesis textarea */}
            <div className="flex-1 flex flex-col">
              <label className="block text-xs text-gray-400 mb-1.5 font-medium uppercase tracking-wide" htmlFor="thesis-text">
                Investment Thesis
              </label>
              <textarea
                id="thesis-text"
                rows={8}
                maxLength={5000}
                placeholder="Why do you hold this position? What's your thesis?"
                value={annotationDraft.thesis}
                onChange={(e) => setAnnotationDraft((d) => ({ ...d, thesis: e.target.value }))}
                className="flex-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm resize-none focus:outline-none focus:border-indigo-500 placeholder-gray-600"
              />
              <p className="text-xs text-gray-500 mt-1 text-right">
                {annotationDraft.thesis.length} / 5,000
              </p>
            </div>

            {/* Error */}
            {annotationStatus && (
              <InlineStatus
                tone={annotationStatus.tone}
                title={annotationStatus.title}
                message={annotationStatus.message}
                compact
              />
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => { void saveAnnotation(); }}
                isLoading={annotationAction === 'save'}
                loadingLabel="Saving thesis…"
                className="flex-1"
              >
                Save Thesis
              </Button>
              {annotations[annotationPanelSymbol] && (
                <Button
                  onClick={() => { void removeAnnotation(); }}
                  isLoading={annotationAction === 'delete'}
                  loadingLabel="Deleting…"
                  variant="danger"
                >
                  Delete
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Compare performance CTA (Chunk 113) */}
      <div className="bg-gray-900 border border-indigo-800/40 rounded-xl p-4 flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-white">Benchmark your portfolio</p>
          <p className="text-xs text-gray-400 mt-0.5">
            Compare against BTC, ETH, or build a custom blend
          </p>
        </div>
        <Link
          to="/comparison"
          className="px-4 py-1.5 rounded-lg text-sm font-medium bg-indigo-600 hover:bg-indigo-500 text-white transition-colors whitespace-nowrap"
        >
          Compare performance →
        </Link>
      </div>

      {/* Link to full attribution */}
      <div className="mt-4">
        <Link to="/attribution" className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1">
          See full attribution analysis →
        </Link>
      </div>

      {/* Link back */}
      <div className="mt-2">
        <Link to="/dashboard" className="text-blue-400 hover:text-blue-300 text-sm">
          ← Back to Dashboard
        </Link>
      </div>
        </>
      )}
    </div>
  );
};

export default PortfolioAnalyticsPage;
